/**
 * Localization Context Provider
 * 
 * Provides localization services to the Experience shell and plugins:
 * - Translation function with ICU MessageFormat support
 * - Intl-based formatting (date, number, currency, relative time, list)
 * - Locale selection and persistence
 * - Plugin catalog loading (on-demand)
 * - RTL support
 * 
 * @module contexts/LocalizationContext
 * @see docs/designs/LOCALIZATION_NORTH_STAR.md
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import type {
  LocalizationContextValue,
  LocaleInfo,
  LocalesResponse,
  CatalogResponse,
  PluginCatalogResponse,
  TranslationCatalog,
  TranslationParams,
} from '../types/i18n';

import { BOOTSTRAP_CATALOG } from '../i18n/bootstrap';
import { fetchWithSWR } from '../utils/fetchWithSWR';

// =============================================================================
// CONSTANTS
// =============================================================================

const STORAGE_KEY = 'empowernow:locale';
const DEFAULT_LOCALE = 'en-US';

// API endpoints (BFF uses /api/v1 prefix)
const API_LOCALES = '/api/v1/i18n/locales';
const API_SHELL_CATALOG = '/api/v1/i18n/shell';
const API_PLUGIN_CATALOG = '/api/v1/i18n/plugins';

// =============================================================================
// SWR Fetch Wrapper (uses shared utility with AbortSignal support)
// =============================================================================

/**
 * Fetch with SWR caching and conditional validators.
 * Wraps shared fetchWithSWR utility with AbortSignal support for i18n.
 */
async function fetchWithCache<T>(
  url: string,
  signal?: AbortSignal,
): Promise<{ data: T; fromCache: boolean }> {
  // Use shared SWR utility with AbortSignal support
  const { data, fromCache } = await fetchWithSWR<T>(
    url,
    {
      signal,
      method: 'GET',
    },
    10_000, // 10 second SWR window
  );
  return { data, fromCache };
}

// =============================================================================
// ICU MESSAGE FORMAT (lazy loaded for bundle size)
// =============================================================================

// Dynamic import to keep initial bundle small
// intl-messageformat is ~15KB gzipped
let IntlMessageFormat: typeof import('intl-messageformat').default | null = null;

async function loadIntlMessageFormat() {
  if (!IntlMessageFormat) {
    const module = await import('intl-messageformat');
    IntlMessageFormat = module.default;
  }
  return IntlMessageFormat;
}

// =============================================================================
// CONTEXT
// =============================================================================

const LocalizationContext = createContext<LocalizationContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface LocalizationProviderProps {
  children: ReactNode;
}

export function LocalizationProvider({ children }: LocalizationProviderProps) {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  
  const [locale, setLocaleState] = useState<string>(() => {
    // Priority: localStorage > browser > default
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return stored;
      
      const browserLocale = navigator.languages?.[0] || navigator.language;
      if (browserLocale) return browserLocale;
    }
    return DEFAULT_LOCALE;
  });
  
  const [resolvedLocale, setResolvedLocale] = useState<string>(DEFAULT_LOCALE);
  const [direction, setDirection] = useState<'ltr' | 'rtl'>('ltr');
  const [shellCatalog, setShellCatalog] = useState<TranslationCatalog>(BOOTSTRAP_CATALOG);
  const [pluginCatalogs, setPluginCatalogs] = useState<Map<string, TranslationCatalog>>(new Map());
  const [availableLocales, setAvailableLocales] = useState<LocaleInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [icuLoaded, setIcuLoaded] = useState(false);
  
  // ---------------------------------------------------------------------------
  // Refs (for caching and request management)
  // ---------------------------------------------------------------------------
  
  const shellRevisionRef = useRef<string>('');
  const pluginRevisionsRef = useRef<Map<string, string>>(new Map());
  const abortControllerRef = useRef<AbortController | null>(null);
  const messageFormatCache = useRef<Map<string, InstanceType<typeof import('intl-messageformat').default>>>(new Map());
  const missingKeysLogged = useRef<Set<string>>(new Set());
  const pluginLoadPromises = useRef<Map<string, Promise<void>>>(new Map());

  // ---------------------------------------------------------------------------
  // Load ICU MessageFormat library
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    loadIntlMessageFormat()
      .then(() => setIcuLoaded(true))
      .catch(err => console.error('[i18n] Failed to load ICU MessageFormat:', err));
  }, []);

  // ---------------------------------------------------------------------------
  // Load available locales on mount (with SWR caching)
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    fetchWithCache<LocalesResponse>(API_LOCALES)
      .then(({ data }) => {
        setAvailableLocales(data.locales || []);
      })
      .catch(err => {
        console.warn('[i18n] Failed to load available locales:', err);
        // Provide minimal fallback
        setAvailableLocales([
          { code: 'en-US', name: 'English', nativeName: 'English', direction: 'ltr' },
        ]);
      });
  }, []);

  // ---------------------------------------------------------------------------
  // Load shell catalog when locale changes (with SWR caching)
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    
    const url = `${API_SHELL_CATALOG}?locale=${encodeURIComponent(locale)}`;
    
    fetchWithCache<CatalogResponse>(url, abortControllerRef.current.signal)
      .then(({ data, fromCache }) => {
        setResolvedLocale(data.resolved_locale);
        setDirection(data.direction);
        
        // Merge with bootstrap catalog (bootstrap is fallback)
        setShellCatalog({ ...BOOTSTRAP_CATALOG, ...data.catalog });
        
        shellRevisionRef.current = data.revision;
        
        // Clear message format cache on locale change (not for SWR hits)
        if (!fromCache) {
          messageFormatCache.current.clear();
        }
        
        setIsLoading(false);
        
        if (import.meta.env.DEV) {
          console.log(`[i18n] Shell catalog loaded (${fromCache ? 'cache' : 'network'}):`, {
            locale: data.resolved_locale,
            keys: Object.keys(data.catalog).length,
          });
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        
        console.warn('[i18n] Failed to load shell catalog:', err);
        // Keep bootstrap catalog - don't crash the app
        setIsLoading(false);
      });

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [locale]);

  // ---------------------------------------------------------------------------
  // Apply direction and lang to document
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = resolvedLocale;
  }, [direction, resolvedLocale]);

  // ---------------------------------------------------------------------------
  // Expose globally for plugins
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    (window as any).__EXPERIENCE_I18N__ = {
      locale,
      resolvedLocale,
      direction,
      shellCatalog,
      pluginCatalogs,
      /**
       * Convenience translation function for plugins that access the global directly.
       * Searches all plugin catalogs then the shell catalog.
       * Prefer sdk.i18n.t() when the SDK is available.
       */
      t: (key: string, params?: Record<string, unknown>): string => {
        // Search plugin catalogs first
        for (const catalog of pluginCatalogs.values()) {
          if (catalog[key]) {
            let msg = catalog[key];
            if (params) {
              Object.entries(params).forEach(([k, v]) => {
                msg = msg.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
              });
            }
            return msg;
          }
        }
        // Fall back to shell catalog
        const shellMsg = shellCatalog[key];
        if (shellMsg) {
          if (!params) return shellMsg;
          let msg = shellMsg;
          Object.entries(params).forEach(([k, v]) => {
            msg = msg.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
          });
          return msg;
        }
        // Return the key for dev visibility
        return key;
      },
    };
  }, [locale, resolvedLocale, direction, shellCatalog, pluginCatalogs]);

  // ---------------------------------------------------------------------------
  // Translation function
  // ---------------------------------------------------------------------------
  
  const t = useCallback((key: string, params?: TranslationParams): string => {
    // Look up message in shell catalog
    const message = shellCatalog[key];
    
    if (!message) {
      // Log missing key (deduplicated, dev only)
      if (import.meta.env.DEV && !missingKeysLogged.current.has(key)) {
        console.warn(`[i18n] Missing translation: ${key}`);
        missingKeysLogged.current.add(key);
      }
      return key;
    }
    
    // Simple case: no params needed
    if (!params || Object.keys(params).length === 0) {
      return message;
    }
    
    // ICU MessageFormat for complex interpolation
    if (icuLoaded && IntlMessageFormat) {
      try {
        const cacheKey = `${resolvedLocale}:${key}`;
        let formatter = messageFormatCache.current.get(cacheKey);
        
        if (!formatter) {
          formatter = new IntlMessageFormat(message, resolvedLocale);
          messageFormatCache.current.set(cacheKey, formatter);
        }
        
        return formatter.format(params) as string;
      } catch (err) {
        console.error(`[i18n] ICU format error for key "${key}":`, err);
        // Fall through to simple replacement
      }
    }
    
    // Fallback: simple {param} replacement
    let text = message;
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    });
    return text;
  }, [shellCatalog, resolvedLocale, icuLoaded]);

  // ---------------------------------------------------------------------------
  // Plugin catalog loading
  // ---------------------------------------------------------------------------
  
  const ensurePluginCatalogLoaded = useCallback(async (pluginId: string): Promise<void> => {
    // Already loaded
    if (pluginCatalogs.has(pluginId)) {
      return;
    }
    
    // Already loading - return existing promise
    const existingPromise = pluginLoadPromises.current.get(pluginId);
    if (existingPromise) {
      return existingPromise;
    }
    
    // Start loading (with SWR caching)
    const loadPromise = (async () => {
      try {
        const url = `${API_PLUGIN_CATALOG}/${encodeURIComponent(pluginId)}?locale=${encodeURIComponent(locale)}`;
        const { data, fromCache } = await fetchWithCache<PluginCatalogResponse>(url);
        
        pluginRevisionsRef.current.set(pluginId, data.revision);
        
        setPluginCatalogs(prev => {
          const next = new Map(prev);
          next.set(pluginId, data.catalog);
          return next;
        });
        
        if (import.meta.env.DEV) {
          console.log(`[i18n] Plugin catalog loaded for "${pluginId}" (${fromCache ? 'cache' : 'network'}):`, {
            keys: Object.keys(data.catalog).length,
          });
        }
      } catch (err) {
        console.warn(`[i18n] Failed to load plugin catalog for "${pluginId}":`, err);
      } finally {
        pluginLoadPromises.current.delete(pluginId);
      }
    })();
    
    pluginLoadPromises.current.set(pluginId, loadPromise);
    return loadPromise;
  }, [locale, pluginCatalogs]);

  const isPluginCatalogLoaded = useCallback((pluginId: string): boolean => {
    return pluginCatalogs.has(pluginId);
  }, [pluginCatalogs]);

  // ---------------------------------------------------------------------------
  // Intl formatters
  // ---------------------------------------------------------------------------
  
  const formatters = useMemo(() => ({
    formatDate: (date: Date | number, options?: Intl.DateTimeFormatOptions) => {
      try {
        return new Intl.DateTimeFormat(resolvedLocale, options).format(date);
      } catch {
        return String(date);
      }
    },
    
    formatNumber: (num: number, options?: Intl.NumberFormatOptions) => {
      try {
        return new Intl.NumberFormat(resolvedLocale, options).format(num);
      } catch {
        return String(num);
      }
    },
    
    formatCurrency: (amount: number, currency = 'USD') => {
      try {
        return new Intl.NumberFormat(resolvedLocale, {
          style: 'currency',
          currency,
        }).format(amount);
      } catch {
        return `${currency} ${amount.toFixed(2)}`;
      }
    },
    
    formatRelativeTime: (value: number, unit: Intl.RelativeTimeFormatUnit) => {
      try {
        return new Intl.RelativeTimeFormat(resolvedLocale, {
          numeric: 'auto',
        }).format(value, unit);
      } catch {
        return `${value} ${unit}`;
      }
    },
    
    formatList: (items: string[], options?: { type?: 'conjunction' | 'disjunction' | 'unit'; style?: 'long' | 'short' | 'narrow' }) => {
      try {
        // ListFormat may not be available in all environments
        const ListFormat = (Intl as any).ListFormat;
        if (ListFormat) {
          return new ListFormat(resolvedLocale, options).format(items);
        }
        return items.join(', ');
      } catch {
        return items.join(', ');
      }
    },
  }), [resolvedLocale]);

  // ---------------------------------------------------------------------------
  // Set locale (with persistence)
  // ---------------------------------------------------------------------------
  
  const setLocale = useCallback((newLocale: string) => {
    localStorage.setItem(STORAGE_KEY, newLocale);
    setLocaleState(newLocale);
    
    // Clear plugin catalogs - they'll reload on-demand in new locale
    setPluginCatalogs(new Map());
    pluginRevisionsRef.current.clear();
    pluginLoadPromises.current.clear();
    
    // Clear missing keys log for fresh reporting
    missingKeysLogged.current.clear();
  }, []);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------
  
  const value: LocalizationContextValue = useMemo(() => ({
    locale,
    resolvedLocale,
    direction,
    isLoading,
    availableLocales,
    setLocale,
    t,
    ...formatters,
    ensurePluginCatalogLoaded,
    isPluginCatalogLoaded,
  }), [
    locale,
    resolvedLocale,
    direction,
    isLoading,
    availableLocales,
    setLocale,
    t,
    formatters,
    ensurePluginCatalogLoaded,
    isPluginCatalogLoaded,
  ]);

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Access the full localization context
 * 
 * @throws Error if used outside LocalizationProvider
 * 
 * @example
 * const { t, formatDate, setLocale } = useI18n();
 */
export function useI18n(): LocalizationContextValue {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useI18n must be used within LocalizationProvider');
  }
  return context;
}

/**
 * Convenience hook for translation function only
 * 
 * @example
 * const { t, locale } = useTranslation();
 */
export function useTranslation() {
  const { t, locale, resolvedLocale } = useI18n();
  return { t, locale, resolvedLocale };
}

/**
 * Convenience hook for formatters only
 * 
 * @example
 * const { formatDate, formatNumber, formatCurrency } = useFormatters();
 */
export function useFormatters() {
  const {
    formatDate,
    formatNumber,
    formatCurrency,
    formatRelativeTime,
    formatList,
    resolvedLocale,
  } = useI18n();
  
  return {
    formatDate,
    formatNumber,
    formatCurrency,
    formatRelativeTime,
    formatList,
    locale: resolvedLocale,
  };
}

/**
 * Hook to ensure a plugin's translations are loaded
 * Returns loading state so component can show skeleton
 * 
 * @example
 * const { isLoaded, isLoading } = usePluginI18n('operations-center');
 * if (isLoading) return <Skeleton />;
 */
export function usePluginI18n(pluginId: string) {
  const { ensurePluginCatalogLoaded, isPluginCatalogLoaded } = useI18n();
  const [isLoading, setIsLoading] = useState(!isPluginCatalogLoaded(pluginId));
  
  useEffect(() => {
    if (!isPluginCatalogLoaded(pluginId)) {
      setIsLoading(true);
      ensurePluginCatalogLoaded(pluginId).finally(() => {
        setIsLoading(false);
      });
    }
  }, [pluginId, ensurePluginCatalogLoaded, isPluginCatalogLoaded]);
  
  return {
    isLoaded: isPluginCatalogLoaded(pluginId),
    isLoading,
  };
}

export default LocalizationProvider;
